import { Archive, Receipt, ShieldCheck } from "lucide-react";
import { fmtMoney } from "@/lib/assessment";

function StatusCard({ icon: Icon, title, status, testid, children }) {
  return (
    <div
      className="rounded-3xl border border-slate-200 bg-white p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-slate-500" />
        <h3 className="text-sm font-medium text-slate-900">{title}</h3>
      </div>
      {status && (
        <p
          className="mt-3 text-2xl font-medium text-slate-900 capitalize"
          data-testid={`${testid}-status`}
        >
          {status}
        </p>
      )}
      {children}
    </div>
  );
}

function MembershipStatus({ sub, busy, onCancel }) {
  const remaining = Math.max(0, sub.min_months - (sub.months_paid || 0));
  return (
    <StatusCard
      icon={ShieldCheck}
      title="Membership status"
      testid="subscribe-status-card"
      status={sub.status}
    >
      <dl className="mt-4 space-y-2 text-sm text-slate-600">
        <div className="flex justify-between">
          <dt>Months paid</dt>
          <dd data-testid="subscribe-months-paid">
            {sub.months_paid} / {sub.min_months} min
          </dd>
        </div>
        <div className="flex justify-between">
          <dt>Can cancel</dt>
          <dd>
            {sub.can_cancel
              ? "Yes"
              : `After ${remaining} more month${remaining === 1 ? "" : "s"}`}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        onClick={onCancel}
        disabled={busy?.plan === "cancel-membership" || !sub.can_cancel}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="subscribe-cancel-button"
      >
        {sub.cancel_at_period_end
          ? "Cancellation scheduled"
          : "Cancel membership"}
      </button>
    </StatusCard>
  );
}

function ArchiveStatus({ archive, busy, onCancel }) {
  const active = archive.status === "active";
  return (
    <StatusCard
      icon={Archive}
      title="Archive status"
      testid="subscribe-archive-status-card"
      status={archive.status}
    >
      <dl className="mt-4 space-y-2 text-sm text-slate-600">
        <div className="flex justify-between">
          <dt>Renewals</dt>
          <dd>{archive.renewals || 0}</dd>
        </div>
        <div className="flex justify-between">
          <dt>Expires</dt>
          <dd data-testid="subscribe-archive-expires">
            {archive.expires_at
              ? new Date(archive.expires_at).toLocaleDateString()
              : "—"}
          </dd>
        </div>
      </dl>
      <button
        type="button"
        onClick={onCancel}
        disabled={busy?.plan === "cancel-archive" || !active}
        className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="subscribe-archive-cancel-button"
      >
        {archive.cancel_at_period_end ? "Auto-renew off" : "Stop auto-renew"}
      </button>
    </StatusCard>
  );
}

function HistoryList({ history }) {
  return (
    <StatusCard
      icon={Receipt}
      title="Billing history"
      testid="subscribe-history-card"
    >
      {history.length === 0 ? (
        <p
          className="mt-3 text-sm text-slate-500 italic"
          data-testid="subscribe-history-empty"
        >
          No charges yet.
        </p>
      ) : (
        <ul
          className="mt-3 space-y-2 text-sm max-h-64 overflow-y-auto"
          data-testid="subscribe-history-list"
        >
          {history.map((t, i) => (
            <li
              key={t.session_id || `tx-${t.created_at || i}`}
              className="flex justify-between gap-2 border-b border-slate-100 pb-2 last:border-0"
            >
              <span className="text-slate-700">
                {new Date(t.created_at).toLocaleDateString()}
              </span>
              <span className="text-slate-500 text-xs uppercase">
                {t.plan || "membership"}
              </span>
              <span className="text-slate-900">
                {fmtMoney(t.amount, (t.currency || "AUD").toUpperCase())}
              </span>
              <span
                className={
                  "text-xs " +
                  (t.payment_status === "paid"
                    ? "text-emerald-700"
                    : "text-slate-500")
                }
              >
                {t.payment_status || t.status}
              </span>
            </li>
          ))}
        </ul>
      )}
    </StatusCard>
  );
}

export default function StatusStrip({
  sub,
  archive,
  history,
  busy,
  onCancelMembership,
  onCancelArchive,
}) {
  return (
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <MembershipStatus sub={sub} busy={busy} onCancel={onCancelMembership} />
      <ArchiveStatus
        archive={archive}
        busy={busy}
        onCancel={onCancelArchive}
      />
      <HistoryList history={history} />
    </div>
  );
}
